import { useState } from 'react'
import { MoreHorizontal, Copy, Plane, Calendar, Fuel, CheckCircle2, AlertTriangle, FileText, MapPin, Clock, User, ShieldCheck, Zap, Coffee, Snowflake, CreditCard, Droplets, Car, AlertCircle, ArrowUpRight, Phone, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '@/components/ui/dropdown-menu'
import { toast } from 'sonner'

// --- Mock Data ---
const MOCK_DATA = {
  tailNumber: 'N750GX',
  type: 'Global 7500',
  category: 'Ultra Long Range',
  owner: 'Nexus Aviation Corp',
  contract: {
    status: 'active',
    type: 'Lease',
    rate: '$12,500/mo',
    expires: 'Nov 15, 2026',
    daysLeft: 245
  },
  location: {
    hangar: 'Hangar B',
    unit: 'Unit 12 (North Wall)'
  },
  status: {
    schedule: 'Dep: 14:00 Today',
    fuel: 'Requested: 1,200g',
    nextEvent: 'De-ice @ 13:30'
  },
  flight: {
    dest: 'KTEB',
    flightNum: 'NXS750',
    quickTurn: true,
    etd: '14:00'
  },
  pax: '4 Pax',
  crew: 'Crew Car: Out',
  services: {
    lav: true,
    gpu: true,
    ice: false,
    catering: true
  },
  financial: {
    balanceDue: true,
    paymentMethod: 'Bill Monthly'
  }
}

// --- Shared Components ---

const performCopy = (text: string, label: string) => {
  navigator.clipboard.writeText(text)
  toast.success(`Copied ${label}`)
}

const ActionMenu = () => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 hover:bg-muted">
        <MoreHorizontal className="h-3 w-3" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuLabel>Quick Actions</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem><FileText className="mr-2 h-3.5 w-3.5" />View Details</DropdownMenuItem>
      <DropdownMenuItem><Calendar className="mr-2 h-3.5 w-3.5" />Schedule</DropdownMenuItem>
      <DropdownMenuItem><Fuel className="mr-2 h-3.5 w-3.5" />Fuel Order</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
)

const CopyButton = ({ text, label }: { text: string, label: string }) => (
  <Button
    variant="ghost"
    size="sm"
    className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity ml-1 text-muted-foreground hover:text-foreground"
    onClick={(e) => {
      e.stopPropagation()
      performCopy(text, label)
    }}
    title="Copy"
  >
    <Copy className="h-2.5 w-2.5" />
  </Button>
)

const ServiceIcons = () => (
  <div className="flex items-center gap-1">
    <TooltipProvider delayDuration={0}>
       <Tooltip>
         <TooltipTrigger><Zap className="h-3 w-3 text-yellow-500 fill-yellow-500" /></TooltipTrigger>
         <TooltipContent>GPU Connected</TooltipContent>
       </Tooltip>
       <Tooltip>
         <TooltipTrigger><Droplets className="h-3 w-3 text-blue-500" /></TooltipTrigger>
         <TooltipContent>Lav Service Req</TooltipContent>
       </Tooltip>
       <Tooltip>
         <TooltipTrigger><Coffee className="h-3 w-3 text-orange-400" /></TooltipTrigger>
         <TooltipContent>Catering Pending</TooltipContent>
       </Tooltip>
    </TooltipProvider>
  </div>
)

// --- Variation 1: List View ---
// Focus: Clarity, Scanability. Good for operational detailed view.
const VariationList = () => {
  return (
    <div className="w-[320px] border rounded-lg shadow-sm bg-card text-card-foreground overflow-hidden">
      {/* Header */}
      <div className="p-3 bg-muted/30 border-b flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2 group">
            <span className="text-lg font-bold">{MOCK_DATA.tailNumber}</span>
            <CopyButton text={MOCK_DATA.tailNumber} label="Tail Number" />
            <Badge variant="secondary" className="text-[10px] h-5">{MOCK_DATA.category}</Badge>
          </div>
          <div className="text-sm text-muted-foreground">{MOCK_DATA.type}</div>
        </div>
        <ActionMenu />
      </div>

      {/* List Content */}
      <div className="p-2 space-y-1">
        {/* Contract Row */}
        <div className="flex items-center justify-between p-2 hover:bg-muted/50 rounded-md group text-sm transition-colors">
          <div className="flex items-center gap-2 text-muted-foreground">
            <FileText className="h-4 w-4" />
            <span className="font-medium text-foreground">Contract</span>
          </div>
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span className="text-green-600 font-medium bg-green-50 px-1.5 py-0.5 rounded text-xs flex items-center gap-1">
                    <CreditCard className="h-3 w-3" />
                    {MOCK_DATA.contract.status.toUpperCase()}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <div>Expires: {MOCK_DATA.contract.expires}</div>
                  <div>Rate: {MOCK_DATA.contract.rate}</div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* Location Row */}
        <div className="flex items-center justify-between p-2 hover:bg-muted/50 rounded-md group text-sm transition-colors">
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="h-4 w-4" />
            <span className="font-medium text-foreground">Location</span>
          </div>
          <div className="flex items-center gap-2">
             <span className="text-xs text-right truncate max-w-[150px]" title={MOCK_DATA.location.unit}>
               {MOCK_DATA.location.unit}
             </span>
          </div>
        </div>

        {/* Schedule / Dest Row */}
        <div className="flex items-center justify-between p-2 hover:bg-muted/50 rounded-md group text-sm transition-colors">
           <div className="flex items-center gap-2 text-muted-foreground">
            <Plane className="h-4 w-4" />
            <span className="font-medium text-foreground">Flight</span>
           </div>
           <div className="flex items-center gap-2 text-xs">
             <Badge variant="outline" className="text-[10px] h-4 border-orange-200 text-orange-700 bg-orange-50 px-1">QT</Badge>
             <span className="font-medium">To {MOCK_DATA.flight.dest}</span>
             <span className="text-muted-foreground">@ {MOCK_DATA.flight.etd}</span>
           </div>
        </div>

        {/* Services & Pax Row */}
        <div className="flex items-center justify-between p-2 hover:bg-muted/50 rounded-md group text-sm transition-colors">
           <div className="flex items-center gap-2 text-muted-foreground">
            <Zap className="h-4 w-4" />
            <span className="font-medium text-foreground">Status</span>
           </div>
            <div className="flex items-center gap-2">
              <ServiceIcons />
              <div className="w-px h-3 bg-border mx-1" />
              <span className="text-xs flex items-center gap-1 text-muted-foreground">
                <User className="h-3 w-3" />{MOCK_DATA.pax}
              </span>
            </div>
        </div>
        
        {/* Financial Warning (if any) */}
         {MOCK_DATA.financial.balanceDue && (
           <div className="px-2 pt-1">
             <div className="text-[10px] text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1 flex items-center gap-1.5">
               <AlertCircle className="h-3 w-3" />
               Outstanding Balance
             </div>
           </div>
         )}

      </div>
    </div>
  )
}


// --- Variation 2: Grid View ---
// Focus: Density. Good for seeing many cards at once.
const VariationGrid = () => {
  return (
    <div className="w-[320px] border rounded-lg shadow-sm bg-card text-card-foreground p-3">
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="flex items-center gap-2 group">
            <h3 className="font-bold text-base">{MOCK_DATA.tailNumber}</h3>
             <CopyButton text={MOCK_DATA.tailNumber} label="Tail Number" />
             {MOCK_DATA.financial.balanceDue && <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" title="Balance Due" />}
          </div>
          <p className="text-xs text-muted-foreground">{MOCK_DATA.type} · {MOCK_DATA.owner}</p>
        </div>
        <ActionMenu />
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="bg-muted/30 p-2 rounded text-xs space-y-1 border border-transparent hover:border-border transition-colors">
          <div className="text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> Location</div>
          <div className="font-medium truncate" title={MOCK_DATA.location.unit}>{MOCK_DATA.location.unit}</div>
        </div>
        <div className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300 p-2 rounded text-xs space-y-1 border border-transparent">
          <div className="opacity-80 flex items-center gap-1"><CreditCard className="h-3 w-3" /> {MOCK_DATA.financial.paymentMethod}</div>
          <div className="font-medium">Active Lease</div>
        </div>
        <div className="bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 p-2 rounded text-xs space-y-1 border border-transparent">
          <div className="opacity-80 flex items-center gap-1"><ArrowUpRight className="h-3 w-3" /> To {MOCK_DATA.flight.dest}</div>
          <div className="font-medium flex items-center justify-between">
             {MOCK_DATA.flight.etd}
             {MOCK_DATA.flight.quickTurn && <Badge variant="secondary" className="text-[9px] h-4 bg-white/50 text-blue-800 px-1">QT</Badge>}
          </div>
        </div>
         <div className="bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300 p-2 rounded text-xs space-y-1 border border-transparent">
          <div className="opacity-80 flex items-center gap-1"><Fuel className="h-3 w-3" /> Fuel Req</div>
          <div className="font-medium">1,200g</div>
        </div>
      </div>
      
       <div className="flex justify-between items-center text-xs text-muted-foreground border-t pt-2">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-muted/50 px-1.5 py-0.5 rounded">
               <User className="h-3 w-3" /> {MOCK_DATA.pax}
            </div>
            <div className="flex items-center gap-1 bg-muted/50 px-1.5 py-0.5 rounded" title="Crew Car Out">
               <Car className="h-3 w-3" /> Out
            </div>
          </div>
          <ServiceIcons />
       </div>

    </div>
  )
}

// --- Variation 3: Grouped View ---
// Focus: Semantic Org. Easy to read sections.
const VariationGrouped = () => {
  return (
    <div className="w-[320px] border rounded-lg shadow-sm bg-card text-card-foreground">
      {/* Identity Section */}
      <div className="p-3 border-b flex justify-between">
         <div>
          <div className="font-black text-lg tracking-tight group flex items-center gap-2">
            {MOCK_DATA.tailNumber}
            <CopyButton text={MOCK_DATA.tailNumber} label="Tail Number" />
            {MOCK_DATA.financial.balanceDue && (
               <Badge variant="destructive" className="text-[9px] h-4 px-1 rounded-sm">Balance Due</Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground font-medium mt-0.5">{MOCK_DATA.owner}</div>
         </div>
         <div className="text-right">
            <div className="text-xs font-medium">{MOCK_DATA.type}</div>
            <div className="flex items-center justify-end gap-1 mt-1">
               <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 border-blue-200 text-blue-700 bg-blue-50">Based</Badge>
               {MOCK_DATA.flight.quickTurn && <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 border-orange-200 text-orange-700 bg-orange-50">QT</Badge>}
            </div>
         </div>
      </div>

      {/* Operation Section */}
      <div className="p-3 space-y-2 bg-muted/10">
         <div className="flex items-center justify-between text-sm group">
            <span className="text-muted-foreground flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> Present</span>
            <span className="font-medium">{MOCK_DATA.location.hangar}</span>
            <ActionMenu />
         </div>
         <DropdownMenuSeparator className="bg-border/50" />
         <div className="flex items-center justify-between text-sm group">
            <span className="text-muted-foreground flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> Schedule</span>
            <div className="flex items-center gap-2">
               <span className="text-xs text-muted-foreground">to {MOCK_DATA.flight.dest}</span>
               <span className="font-medium text-blue-600">{MOCK_DATA.flight.etd}</span>
            </div>
            <ActionMenu />
         </div>
      </div>

      {/* Footer Indicators */}
      <div className="px-3 py-2 bg-muted/30 border-t flex items-center justify-between">
         
         <div className="flex items-center gap-2">
            <TooltipProvider>
                <Tooltip>
                <TooltipTrigger>
                    <Badge variant="outline" className="bg-background text-xs font-normal gap-1 hover:bg-accent cursor-help pl-1.5 border-orange-200 text-orange-700">
                    <Fuel className="h-3 w-3 text-orange-500 fill-orange-500" /> 1,200g
                    </Badge>
                </TooltipTrigger>
                <TooltipContent>Fuel Requested: 1,200 gallons Jet-A</TooltipContent>
                </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
                <Tooltip>
                <TooltipTrigger>
                    <Badge variant="outline" className="bg-background text-xs font-normal gap-1 hover:bg-accent cursor-help pl-1.5 border-blue-200 text-blue-700">
                    <Snowflake className="h-3 w-3 text-blue-500" /> De-ice
                    </Badge>
                </TooltipTrigger>
                <TooltipContent>De-icing scheduled for 13:30</TooltipContent>
                </Tooltip>
            </TooltipProvider>
         </div>

         <div className="flex items-center gap-2 opacity-70">
            <span className="flex items-center gap-1 text-[10px] font-medium"><User className="h-3 w-3" /> 4</span>
            <span className="flex items-center gap-1 text-[10px] font-medium"><Car className="h-3 w-3" /> Out</span>
         </div>
      </div>
    </div>
  )
}

// --- Variation 4: Minimal / Visual ---
// Focus: "Card" feel, less label text, relying on layout convention.
const VariationMinimal = () => {
  return (
    <div className="w-[320px] border rounded-xl shadow-sm bg-card text-card-foreground p-4 relative overflow-hidden group/card">
      <div className="absolute top-0 right-0 p-2 opacity-0 group-hover/card:opacity-100 transition-opacity z-10">
         <ActionMenu />
      </div>

      {MOCK_DATA.financial.balanceDue && (
         <div className="absolute top-0 left-0 right-0 h-1 bg-red-500" title="Outstanding Balance" />
      )}

      <div className="flex flex-col gap-1 mb-4">
         <div className="flex items-baseline gap-2 group">
            <span className="text-2xl font-bold tracking-tight">{MOCK_DATA.tailNumber}</span>
            <span className="text-sm text-muted-foreground">{MOCK_DATA.type}</span>
         </div>
         <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="truncate max-w-[180px] text-foreground font-medium">{MOCK_DATA.owner}</span>
            <span className="text-border">•</span>
            {MOCK_DATA.flight.quickTurn && <span className="text-orange-600 font-bold">QT</span>}
            <span className="text-border">•</span>
            <span className="text-green-600 font-medium">Active</span>
         </div>
      </div>

      <div className="space-y-3">
         {/* Location Pill - Prominent */}
         <div className="flex items-center gap-2 bg-secondary/50 p-2 rounded-lg">
            <div className="h-8 w-8 rounded-full bg-background flex items-center justify-center shadow-sm shrink-0">
               <MapPin className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
               <div className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Located At</div>
               <div className="text-sm font-medium truncate">{MOCK_DATA.location.unit}</div>
            </div>
         </div>

         {/* Flights / Ops */}
         <div className="flex items-center gap-2">
            <div className="flex-1 bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg border border-blue-100 dark:border-blue-900">
               <div className="text-[10px] text-blue-600 dark:text-blue-400 font-bold uppercase mb-0.5 flex justify-between">
                 Departs <Plane className="h-3 w-3" />
               </div>
               <div className="text-sm font-semibold">{MOCK_DATA.flight.etd}</div>
               <div className="text-[10px] text-muted-foreground">to {MOCK_DATA.flight.dest}</div>
            </div>
            <div className="flex-1 bg-orange-50 dark:bg-orange-900/20 p-2 rounded-lg border border-orange-100 dark:border-orange-900">
               <div className="text-[10px] text-orange-600 dark:text-orange-400 font-bold uppercase mb-0.5 flex justify-between">
                 Fuel <Fuel className="h-3 w-3" />
               </div>
               <div className="text-sm font-semibold">1,200g</div>
               <div className="text-[10px] text-muted-foreground">Requested</div>
            </div>
         </div>
         
         {/* Services */}
         <div className="flex gap-1.5 flex-wrap items-center">
             <Badge variant="secondary" className="text-[10px] h-5 gap-1"><User className="h-3 w-3" /> 4</Badge>
             <Badge variant="secondary" className="text-[10px] h-5 gap-1"><Car className="h-3 w-3" /> Crew</Badge>
             <div className="w-px h-4 bg-border mx-1" />
             <ServiceIcons />
         </div>
      </div>
    </div>
  )
}



// --- Variation 5: User Recommended Layout ---
const VariationRecommended = () => {
  return (
    <div className="w-[320px] border rounded-lg shadow-sm bg-card text-card-foreground overflow-hidden flex flex-col">
      {/* 1. Header Block: Identity & Location */}
      <div className="p-3 pb-2">
        <div className="flex justify-between items-start">
           <div>
              <div className="flex items-baseline gap-2">
                <span className="font-bold text-lg">{MOCK_DATA.tailNumber}</span>
                <span className="text-muted-foreground text-sm">{MOCK_DATA.type}</span>
              </div>
              <Badge variant="secondary" className="text-[10px] h-4 mt-0.5 px-1">{MOCK_DATA.category}</Badge>
           </div>
           <div className="flex gap-0.5">
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0"><ArrowUpRight className="h-3.5 w-3.5" /></Button>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0"><ActionMenu /></Button>
           </div>
        </div>
        
        <div className="mt-2 space-y-0.5">
           <div className="flex items-center gap-2 text-sm">
              <span className="font-medium">{MOCK_DATA.owner}</span>
              <TooltipProvider>
                 <Tooltip>
                   <TooltipTrigger><Phone className="h-3 w-3 text-muted-foreground hover:text-primary cursor-pointer" /></TooltipTrigger>
                   <TooltipContent>Call (480) 555-0199</TooltipContent>
                 </Tooltip>
              </TooltipProvider>
           </div>
           <div className="text-xs text-muted-foreground flex items-center gap-1">
              {MOCK_DATA.location.hangar} · {MOCK_DATA.location.unit}
           </div>
        </div>
      </div>

      {/* 2. Warnings Bar */}
      <div className="border-t border-b divide-y bg-red-50/50">
         {/* Balance Warning */}
         {MOCK_DATA.financial.balanceDue && (
           <div className="px-3 py-1.5 flex items-center justify-between group cursor-pointer hover:bg-red-50 transition-colors">
              <div className="flex items-center gap-2 text-xs text-red-700 font-medium">
                 <AlertTriangle className="h-3 w-3" />
                 To Pay: $4,200
              </div>
              <ChevronRight className="h-3 w-3 text-red-400 group-hover:text-red-600" />
           </div>
         )}
         {/* Contract Warning (if expired) - Mocking expired for this view */}
         <div className="px-3 py-1.5 flex items-center justify-between group cursor-pointer hover:bg-red-50 transition-colors">
            <div className="flex items-center gap-2 text-xs text-red-700 font-medium">
               <FileText className="h-3 w-3" />
               Lease Expired · 4d ago
            </div>
            <ChevronRight className="h-3 w-3 text-red-400 group-hover:text-red-600" />
         </div>
      </div>

      {/* 3. Departure Line (High Impact) */}
      <div className="px-3 py-2 bg-blue-50/30 flex items-center justify-between border-b">
         <div className="flex items-center gap-2">
            <Plane className="h-3.5 w-3.5 text-blue-600 rotate-45" />
            <span className="text-sm font-semibold text-foreground">Dep: 14:00</span>
            <span className="text-xs text-muted-foreground">→ {MOCK_DATA.flight.dest}</span>
         </div>
         <div className="flex items-center gap-1.5">
            <Badge variant="outline" className="text-[10px] h-4 bg-white px-1 border-blue-200 text-blue-700">QT</Badge>
            <span className="text-xs font-medium text-muted-foreground">4 Pax</span>
         </div>
      </div>

      {/* 4. Status Pills Row */}
      <div className="px-3 py-2 flex items-center gap-2 border-b bg-muted/5">
         <div className="flex items-center gap-1 text-[10px] font-medium text-green-700 bg-green-50 px-1.5 py-0.5 rounded border border-green-100">
            <CheckCircle2 className="h-3 w-3" /> Contract
         </div>
         <div className="flex items-center gap-1 text-[10px] font-medium text-orange-700 bg-orange-50 px-1.5 py-0.5 rounded border border-orange-100">
            <Fuel className="h-3 w-3" /> Fuel Req
         </div>
          <div className="flex items-center gap-1 text-[10px] font-medium text-red-700 bg-red-50 px-1.5 py-0.5 rounded border border-red-100">
            <CreditCard className="h-3 w-3" /> Balance
         </div>
      </div>

      {/* 5. Actions */}
      <div className="p-2 flex gap-1 justify-between bg-muted/10 mt-auto">
         <Button variant="outline" size="sm" className="h-7 text-xs flex-1">Move</Button>
         <Button variant="outline" size="sm" className="h-7 text-xs flex-1">Depart</Button>
         <Button variant="outline" size="sm" className="h-7 text-xs flex-1">Fuel</Button>
         <Button variant="outline" size="sm" className="h-7 w-7 p-0"><MoreHorizontal className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  )
}

// --- Variation 6: Smart Context (Interactive) ---
const VariationSmartContext = () => {
    const [isBusy, setIsBusy] = useState(true)

    // Derived mock state based on toggle
    const currentData = isBusy ? MOCK_DATA : {
        ...MOCK_DATA,
        status: { schedule: null, fuel: null, nextEvent: null },
        flight: { ...MOCK_DATA.flight, etd: null, quickTurn: false },
        financial: { balanceDue: false },
        services: { lav: false, gpu: false, catering: false },
        contract: { ...MOCK_DATA.contract, status: 'active', daysLeft: 245 } // Healthy
    }

    return (
        <div className="flex flex-col gap-2 w-[320px]">
             
            <div className="w-full border rounded-lg shadow-sm bg-card text-card-foreground overflow-hidden flex flex-col transition-all duration-300">
                {/* Header - Always Clean */}
                <div className="p-3 border-b flex justify-between items-start">
                    <div>
                        <div className="flex items-center gap-2">
                             <span className="font-bold text-lg">{currentData.tailNumber}</span>
                             <span className="text-xs text-muted-foreground">{currentData.type}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">{currentData.owner}</div>
                        <div className="text-xs mt-1 font-medium">{currentData.location.unit}</div>
                    </div>
                     <ActionMenu />
                </div>

                {/* Smart Section: Operational Context */}
                {(currentData.flight.etd || currentData.services.lav) ? (
                    <div className="bg-blue-50/20 p-3 border-b animate-in slide-in-from-top-2 duration-300">
                         {currentData.flight.etd && (
                            <div className="flex justify-between items-center mb-2">
                                <div className="flex items-center gap-2">
                                    <div className="bg-blue-100 text-blue-700 p-1 rounded-sm"><Plane className="h-3.5 w-3.5 rotate-45" /></div>
                                    <div>
                                        <div className="text-xs font-bold text-blue-900 leading-none">Dep: {currentData.flight.etd}</div>
                                        <div className="text-[10px] text-blue-700">to {currentData.flight.dest}</div>
                                    </div>
                                </div>
                                {currentData.flight.quickTurn && (
                                    <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 border-orange-200 text-[10px] h-5 shadow-none">
                                        Quick Turn
                                    </Badge>
                                )}
                            </div>
                         )}
                         
                         {/* Service Icons - Only visible if active */}
                         <div className="flex gap-2">
                             {currentData.services.lav && <Badge variant="secondary" className="h-5 text-[10px] gap-1 bg-white"><Droplets className="h-3 w-3 text-blue-500" /> Lav</Badge>}
                             {currentData.services.catering && <Badge variant="secondary" className="h-5 text-[10px] gap-1 bg-white"><Coffee className="h-3 w-3 text-orange-500" /> Cater</Badge>}
                             {currentData.services.gpu && <Badge variant="secondary" className="h-5 text-[10px] gap-1 bg-white"><Zap className="h-3 w-3 text-yellow-500" /> GPU</Badge>}
                         </div>
                    </div>
                ) : (
                    // Quiet State Placeholder
                     <div className="px-3 py-2 bg-muted/5 border-b text-xs text-muted-foreground italic flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3" /> All operations normal
                     </div>
                )}

                 {/* Contract / Financial Warning Row - Only appears if actionable */}
                 {(currentData.financial.balanceDue || currentData.contract.daysLeft < 30) ? (
                    <div className="px-3 py-2 bg-red-50 border-b flex justify-between items-center animate-in fade-in duration-300">
                         <div className="flex gap-3">
                            {currentData.financial.balanceDue && (
                                <div className="text-[10px] font-bold text-red-700 flex items-center gap-1"><CreditCard className="h-3 w-3" /> Balance Due</div>
                            )}
                             {currentData.contract.daysLeft < 30 && (
                                <div className="text-[10px] font-bold text-orange-700 flex items-center gap-1"><FileText className="h-3 w-3" /> Renew Lease</div>
                            )}
                         </div>
                         <Button size="sm" variant="ghost" className="h-5 w-5 p-0 hover:bg-red-100"><ChevronRight className="h-3 w-3 text-red-700" /></Button>
                    </div>
                 ) : (
                    // Healthy Contract State
                    <div className="px-3 py-1.5 flex justify-between items-center border-b">
                         <div className="text-[10px] text-green-700 font-medium flex items-center gap-1">
                             <ShieldCheck className="h-3 w-3" /> Contract Active
                         </div>
                    </div>
                 )}

                 {/* Action Bar */}
                 <div className="grid grid-cols-3 gap-px bg-border text-xs font-medium text-center">
                    <div className="bg-card hover:bg-muted/50 py-2 cursor-pointer transition-colors">Move</div>
                    <div className="bg-card hover:bg-muted/50 py-2 cursor-pointer transition-colors">Fuel</div>
                    <div className="bg-card hover:bg-muted/50 py-2 cursor-pointer transition-colors">More</div>
                 </div>

            </div>
            
            <div className="flex items-center justify-center gap-2 mt-2 bg-muted/30 p-1.5 rounded-lg">
                <span className="text-xs font-medium text-muted-foreground">Simulation:</span>
                <Button 
                    variant={isBusy ? "default" : "outline"} 
                    size="sm" 
                    className="h-6 text-[10px]"
                    onClick={() => setIsBusy(true)}
                >
                    Busy Day
                </Button>
                <Button 
                    variant={!isBusy ? "default" : "outline"} 
                    size="sm" 
                    className="h-6 text-[10px]"
                    onClick={() => setIsBusy(false)}
                >
                    Quiet Day
                </Button>
            </div>
        </div>
    )
}

export function CardHeaderDemos() {
  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 p-8">
      <div className="max-w-[70vw] mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight mb-2">Card Header Variations</h1>
          <p className="text-muted-foreground">
            Comparing different layouts for the aircraft hangar card. Hover over elements to see interactions.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8 items-start">
          
          <div className="space-y-4">
             <div className="px-1 border-l-4 border-primary/20 pl-3">
                <h2 className="font-semibold text-lg">1. List View</h2>
                <p className="text-sm text-muted-foreground">Focus on scanning specific data points vertically.</p>
             </div>
             <VariationList />
          </div>

          <div className="space-y-4">
             <div className="px-1 border-l-4 border-primary/20 pl-3">
                <h2 className="font-semibold text-lg">2. Grid View</h2>
                <p className="text-sm text-muted-foreground">High density info in a compact 2-column grid.</p>
             </div>
             <VariationGrid />
          </div>

          <div className="space-y-4">
             <div className="px-1 border-l-4 border-primary/20 pl-3">
                <h2 className="font-semibold text-lg">3. Grouped View</h2>
                <p className="text-sm text-muted-foreground">Semantic sections with visual hierarchy.</p>
             </div>
             <VariationGrouped />
          </div>

           <div className="space-y-4">
             <div className="px-1 border-l-4 border-primary/20 pl-3">
                <h2 className="font-semibold text-lg">4. Minimal/Visual</h2>
                <p className="text-sm text-muted-foreground">Modern card feel with highlighted actionable data.</p>
             </div>
             <VariationMinimal />
          </div>

          <div className="space-y-4">
             <div className="px-1 border-l-4 border-green-500 pl-3">
                <h2 className="font-semibold text-lg">5. User Recommended</h2>
                <p className="text-sm text-muted-foreground">Specific hierarchy: ID &rarr; Warnings &rarr; Ops &rarr; Actions.</p>
             </div>
             <VariationRecommended />
          </div>

           <div className="space-y-4">
             <div className="px-1 border-l-4 border-blue-500 pl-3">
                <h2 className="font-semibold text-lg">6. Smart Context</h2>
                <p className="text-sm text-muted-foreground">Adaptive UI. Hides visual noise when not urgent.</p>
             </div>
             <VariationSmartContext />
          </div>

        </div>
      </div>
    </div>
  )
}

export default CardHeaderDemos
